import React, { useEffect, useState } from "react";
import { Button, Form, Input, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import apiClient from "../../actions/api";
import openNotification from "../../components/Notifications/openNotification";
import isEmpty from "../../validations/is-empty";
import {
  InfoCircleOutlined,
  LockOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import LogoSrc from "../../assets/image/logo.svg";
import "../../style/auth/auth.scss";

const Register = ({ title }) => {
  // set title
  document.title = title;

  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authUser.isAuthenticated) {
      navigate("/dashboard");
    }
  }, [authUser, navigate]);

  const onFinish = (values) => {
    if (values.password !== values.cpassword) {
      openNotification(
        "error",
        "Validation Error!",
        `Please check the confirm password`,
      );
      return;
    }

    setLoading(true);

    apiClient
      .post("/users/register", values)
      .then((res) => {
        if (!isEmpty(res.data)) {
          if (res.data.status === "success") {
            localStorage.setItem(
              "resumeBuilderPendingWelcomeTour",
              String(values.email || "")
                .trim()
                .toLowerCase(),
            );
            navigate("/login");
            openNotification("success", "Success", "You've signed up.");
          } else {
            openNotification("error", "Server Error!", "Server Error!");
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  const onFinishFailed = () => {
    openNotification("error", "Validation Error!", "Something went wrong!");
  };

  const onGoTo = () => {
    navigate("/login");
  };

  return (
    <div className="product-auth-page">
      <div className="product-auth-shell">
        <aside className="product-auth-brand">
          <div className="product-auth-logo">
            <img src={LogoSrc} alt="" />
            <span>
              Resume <strong>Builder</strong>
            </span>
          </div>
          <div className="product-auth-brand-copy">
            <span className="product-auth-eyebrow">A Lovapextech product</span>
            <h1>Your career workflow starts here.</h1>
            <p>
              Create tailored resumes, track every opportunity, and collaborate
              with your team from one focused workspace.
            </p>
          </div>
          <div className="product-auth-trust">
            <SafetyCertificateOutlined />
            <span>
              <strong>Built for focused teams</strong>
              <small>Simple onboarding with secure account access.</small>
            </span>
          </div>
        </aside>
        <section className="product-auth-card product-auth-card-register">
          <div className="product-auth-heading">
            <span>Get started</span>
            <h2>Create your account</h2>
            <p>Set up your Resume Builder workspace in a few steps.</p>
          </div>
          <Form
            name="basic"
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[{ required: true, message: "Please input your email!" }]}
            >
              <Input
                size="large"
                prefix={<MailOutlined />}
                placeholder="you@company.com"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              label={
                <span className="product-auth-label-with-tip">
                  Full Name
                  <Tooltip title="Your full name will be used as the file name for your generated resume.">
                    <InfoCircleOutlined
                      aria-label="Why we need your full name"
                      tabIndex={0}
                    />
                  </Tooltip>
                </span>
              }
              name="name"
              rules={[
                { required: true, message: "Please input your full name!" },
              ]}
            >
              <Input
                size="large"
                prefix={<UserOutlined />}
                placeholder="Your full name"
                autoComplete="name"
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Please input your password!" },
              ]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Create a password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              label="Confirm Password"
              name="cpassword"
              rules={[
                {
                  required: true,
                  message: "Please input your confirm password!",
                },
              ]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Repeat your password"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item className="product-auth-submit">
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={loading}
              >
                Create Account
              </Button>
            </Form.Item>
          </Form>
          <div className="product-auth-switch">
            Already have an account?{" "}
            <Button type="link" htmlType="button" onClick={onGoTo}>
              Sign in
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Register;
